/**
 * "Option name: value" lines from Dawn variant-selects (dropdown, pill, swatch).
 */
function collectQuickAddVariantOptionLines(root) {
  if (!root || typeof root.querySelector !== 'function') return [];
  const variantSelects = root.querySelector('variant-selects');
  if (!variantSelects) return [];

  const blocks = variantSelects.querySelectorAll(
    ':scope > fieldset.product-form__input, :scope > .product-form__input--dropdown'
  );
  const lines = [];

  blocks.forEach((el) => {
    if (el.tagName === 'FIELDSET') {
      const leg = el.querySelector('legend.form__label');
      const selSpan = leg?.querySelector('[data-selected-value]');
      if (leg && selSpan) {
        let name = '';
        for (const node of leg.childNodes) {
          if (node === selSpan) break;
          if (node.nodeType === Node.TEXT_NODE) name += node.textContent;
        }
        name = name.replace(/\s*:\s*$/, '').trim();
        const val = (selSpan.textContent || '').trim();
        if (name && val) lines.push(`${name}: ${val}`);
        return;
      }
      if (leg) {
        const name = (leg.textContent || '').trim().replace(/\s*:\s*$/, '');
        const checked = el.querySelector('input[type="radio"]:checked');
        if (name && checked?.value) lines.push(`${name}: ${checked.value}`);
      }
    } else {
      const lab = el.querySelector('label.form__label');
      const sel = el.querySelector('select');
      if (lab && sel) {
        const name = (lab.textContent || '').replace(/\s*:\s*$/, '').trim();
        const opt = sel.options[sel.selectedIndex];
        const val = (opt?.textContent || sel.value || '').trim();
        if (name && val) lines.push(`${name}: ${val}`);
      }
    }
  });

  return lines;
}

if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.variantIdInput.disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText =
          this.submitButton.querySelector('.product-form__submit-label') || this.submitButton.querySelector('span');

        if (
          document.querySelector('cart-drawer') &&
          !this.closest('.guide-driver-part-card')
        ) {
          this.submitButton.setAttribute('aria-haspopup', 'dialog');
        }

        this.hideErrors = this.dataset.hideErrors === 'true';
      }

      /**
       * `cart-icon-bubble` section HTML only contains the inner contents of the header cart link.
       * Never replace the `<a id="cart-icon-bubble" class="header__icon ...">` element itself, or
       * theme styling (icon color/size) is lost.
       */
      updateCartIconBubbleFromSectionHtml(sectionHtml) {
        const bubble = document.getElementById('cart-icon-bubble');
        if (!bubble || !sectionHtml) return;

        const doc = new DOMParser().parseFromString(sectionHtml, 'text/html');
        const next = doc.querySelector('.shopify-section');
        if (!next) return;

        const nextSvg = next.querySelector('.svg-wrapper');
        const nextHidden = next.querySelector('.visually-hidden');
        const nextCount = next.querySelector('.cart-count-bubble');

        const curSvg = bubble.querySelector('.svg-wrapper');
        const curHidden = bubble.querySelector('.visually-hidden');
        const curCount = bubble.querySelector('.cart-count-bubble');

        if (nextSvg && curSvg) curSvg.replaceWith(nextSvg);
        if (nextHidden && curHidden) curHidden.replaceWith(nextHidden);

        if (nextCount) {
          if (curCount) curCount.replaceWith(nextCount);
          else bubble.appendChild(nextCount);
        } else if (curCount) {
          curCount.remove();
        }
      }

      formatCartAddErrorMessage(response) {
        if (!response) return '';
        const d = response.description;
        if (typeof d === 'string' && d.trim()) return d.trim();
        if (d && typeof d === 'object') {
          const nested = d.base || d.quantity || d.message;
          if (typeof nested === 'string' && nested.trim()) return nested.trim();
          if (Array.isArray(nested) && nested[0]) return String(nested[0]).trim();
        }
        const msg = response.message;
        if (typeof msg === 'string' && msg.trim()) return msg.trim();
        const errors = response.errors;
        if (typeof errors === 'string' && errors.trim()) return errors.trim();
        if (errors && typeof errors === 'object') {
          for (const v of Object.values(errors)) {
            if (Array.isArray(v) && v.length && v[0]) return String(v[0]).trim();
            if (typeof v === 'string' && v.trim()) return v.trim();
          }
        }
        return '';
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

        this.handleErrorMessage();

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        this.querySelector('.loading__spinner').classList.remove('hidden');

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData(this.form);
        if (this.cart) {
          formData.append(
            'sections',
            this.cart.getSectionsToRender().map((section) => section.id)
          );
          formData.append('sections_url', window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }
        config.body = formData;

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              const errMsg = this.formatCartAddErrorMessage(response);
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(errMsg || false);

              const quickAddErrCtx = this.closest('quick-add-modal') || this.closest('.guide-driver-part-card');
              if (quickAddErrCtx) {
                this.showQuickAddToastError(errMsg || window.cartStrings?.error || 'Could not add to cart.');
              }

              const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
              if (!soldOutMessage) return;
              this.submitButton.setAttribute('aria-disabled', true);
              this.submitButtonText.classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              return;
            } else if (!this.cart) {
              window.location = window.routes.cart_url;
              return;
            }

            const startMarker = CartPerformance.createStartingMarker('add:wait-for-subscribers');
            if (!this.error) {
              try {
                publish(PUB_SUB_EVENTS.cartUpdate, {
                  source: 'product-form',
                  productVariantId: formData.get('id'),
                  cartData: response,
                })
                  .then(() => {
                    CartPerformance.measureFromMarker('add:wait-for-subscribers', startMarker);
                  })
                  .catch((err) => console.error(err));
              } catch (err) {
                console.error(err);
              }
            }
            this.error = false;
            const quickAddModal = this.closest('quick-add-modal');
            const guidePartCard = this.closest('.guide-driver-part-card');
            const silentCartToastAdd = quickAddModal || guidePartCard;
            if (silentCartToastAdd) {
              // Quick-add modal OR Guide driver-build part cards:
              // - Do NOT open cart drawer
              // - Show bottom-right toast (same as quick-add add-to-cart)
              CartPerformance.measure("add:paint-updated-sections", () => {
                try {
                  const sections = response?.sections || {};

                  // Update cart icon bubble count
                  if (sections['cart-icon-bubble']) {
                    this.updateCartIconBubbleFromSectionHtml(sections['cart-icon-bubble']);
                  }

                  // Update cart drawer HTML without opening it
                  if (sections['cart-drawer']) {
                    const drawerRoot = document.getElementById('CartDrawer');
                    const drawerDoc = new DOMParser().parseFromString(sections['cart-drawer'], 'text/html');
                    const drawerHtml = drawerDoc.querySelector('#CartDrawer')?.innerHTML;
                    if (drawerRoot && drawerHtml) drawerRoot.innerHTML = drawerHtml;
                  }
                } catch (e) {
                  console.error(e);
                }
              });

              let title = '';
              let variantLines = [];
              const addedVariantId = formData.get('id');

              if (response?.items?.length && addedVariantId) {
                const addedItem = response.items.find(
                  (item) => String(item.variant_id) === String(addedVariantId)
                );
                const owv = addedItem?.options_with_values;
                if (Array.isArray(owv) && owv.length) {
                  variantLines = owv
                    .map((o) => {
                      const n = (o?.name || '').trim();
                      const v = (o?.value || '').trim();
                      return n && v ? `${n}: ${v}` : '';
                    })
                    .filter(Boolean);
                }
              }

              if (quickAddModal) {
                title = (
                  quickAddModal.querySelector('.quick-add-sticky-header__title')?.textContent ||
                  quickAddModal.querySelector('.product__title')?.textContent ||
                  ''
                ).trim();
                let fromDom = [];
                try {
                  fromDom = collectQuickAddVariantOptionLines(quickAddModal);
                } catch (err) {
                  console.error(err);
                }
                if (fromDom.length) {
                  variantLines = fromDom;
                } else if (!variantLines.length && addedVariantId && response?.items?.length) {
                  const addedItem = response.items.find(
                    (item) => String(item.variant_id) === String(addedVariantId)
                  );
                  const vt = (addedItem?.variant_title || '').trim();
                  if (vt) variantLines = [vt];
                }
                if (!variantLines.length) {
                  try {
                    const sel = quickAddModal.querySelector('variant-selects [data-selected-variant]');
                    if (sel?.textContent) {
                      const v = JSON.parse(sel.textContent);
                      if (Array.isArray(v.options) && v.options.length) {
                        variantLines = [v.options.filter(Boolean).join(' · ')];
                      }
                    }
                  } catch (e) {
                    /* ignore */
                  }
                }
              } else if (guidePartCard) {
                title =
                  guidePartCard.querySelector('.guide-driver-part-card__title')?.textContent?.trim() || '';
                let fromDom = [];
                try {
                  fromDom = collectQuickAddVariantOptionLines(guidePartCard);
                } catch (err) {
                  console.error(err);
                }
                if (fromDom.length) variantLines = fromDom;
              }

              this.showQuickAddToast(title || 'Product', variantLines.join('\n'));
            } else {
              CartPerformance.measure("add:paint-updated-sections", () => {
                this.cart.renderContents(response);
              });
            }
          })
          .catch((e) => {
            console.error(e);
          })
          .finally(() => {
            this.submitButton.classList.remove('loading');
            if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
            if (!this.error) this.submitButton.removeAttribute('aria-disabled');
            this.querySelector('.loading__spinner').classList.add('hidden');

            CartPerformance.measureFromEvent("add:user-action", evt);
          });
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text) {
        const quickAddModal = this.closest('quick-add-modal');
        const opener = quickAddModal?.openedBy;
        const guidePartShell = quickAddModal?.closest('.guide-driver-part-card-shell');
        const isGuidePartComingSoon =
          opener?.classList.contains('guide-driver-part-card__details') &&
          (opener.getAttribute('data-product-coming-soon') === 'true' ||
            guidePartShell?.getAttribute('data-product-coming-soon') === 'true');

        if (isGuidePartComingSoon) {
          this.submitButton.setAttribute('disabled', 'disabled');
          this.submitButtonText.textContent = window.variantStrings.comingSoon || 'Coming soon';
          return;
        }

        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          if (text) this.submitButtonText.textContent = text;
        } else {
          this.submitButton.removeAttribute('disabled');
          this.submitButtonText.textContent = window.variantStrings.addToCart;
        }
      }

      showQuickAddToast(productTitle, variantOptionsText = '') {
        const id = 'QuickAddToast';
        let toast = document.getElementById(id);
        if (!toast) {
          toast = document.createElement('div');
          toast.id = id;
          toast.className = 'quick-add-toast';
          toast.setAttribute('role', 'status');
          toast.setAttribute('aria-live', 'polite');
          document.body.appendChild(toast);
        }

        const main = String(productTitle || '').trim() || 'Product';
        const opts = String(variantOptionsText || '').trim();
        const duplicate =
          opts &&
          (main === opts ||
            opts.startsWith(`${main} - `) ||
            opts.startsWith(`${main} — `) ||
            main.endsWith(` ${opts}`));
        const optionLine = duplicate ? '' : opts;

        const formatOptionLines = (raw) => {
          const t = raw.trim();
          if (!t) return '';
          if (t.includes('\n')) {
            return t
              .split('\n')
              .map((l) => l.trim())
              .filter(Boolean)
              .join('\n');
          }
          if (/^[^\n:]+:\s*\S/.test(t)) return t;
          const bySlash = t.split(/\s*\/\s*/).map((p) => p.trim()).filter(Boolean);
          if (bySlash.length > 1) return bySlash.join('\n');
          const byDot = t.split(/\s*·\s*/).map((p) => p.trim()).filter(Boolean);
          if (byDot.length > 1) return byDot.join('\n');
          return t;
        };

        let message;
        if (optionLine) {
          message = `${main}\n${formatOptionLines(optionLine)}\nhas been added to cart!`;
        } else {
          message = `${main} has been added to cart!`;
        }

        toast.classList.remove('quick-add-toast--error');
        toast.setAttribute('role', 'status');
        toast.setAttribute('aria-live', 'polite');
        toast.textContent = message;
        toast.classList.add('is-active');

        window.clearTimeout(window.__quickAddToastTimer);
        window.__quickAddToastTimer = window.setTimeout(() => {
          toast.classList.remove('is-active');
        }, 3000);
      }

      showQuickAddToastError(message) {
        const id = 'QuickAddToast';
        let toast = document.getElementById(id);
        if (!toast) {
          toast = document.createElement('div');
          toast.id = id;
          toast.className = 'quick-add-toast';
          document.body.appendChild(toast);
        }

        const text =
          String(message || '').trim() || window.cartStrings?.error || 'Could not add to cart.';
        toast.classList.add('quick-add-toast--error');
        toast.setAttribute('role', 'alert');
        toast.setAttribute('aria-live', 'assertive');
        toast.textContent = text;
        toast.classList.add('is-active');

        window.clearTimeout(window.__quickAddToastTimer);
        window.__quickAddToastTimer = window.setTimeout(() => {
          toast.classList.remove('is-active');
        }, 5500);
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }
    }
  );
}
